import { isPresent } from '@jjvgaming/player-payback-library'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useTypedApiClient } from 'hooks/useTypedApiClient'
import { type Resource } from 'types/api'

const path = '/admin/resources/{id}'
const method = 'put'

interface UsePutResourceParams {
  resourceId: number
  onSuccess?: () => void
  onError?: () => void
}

type WritableResource = Resource & {
  id: number
}

const isResourceWritable = (
  resource: Resource
): resource is WritableResource => {
  return isPresent(resource.id)
}

export const usePutResource = ({
  resourceId,
  onSuccess,
  onError,
}: UsePutResourceParams) => {
  const queryClient = useQueryClient()
  const put = useTypedApiClient({ path, method })
  const mutate = useMutation({
    mutationKey: ['/admin/resources/{id}', resourceId],
    mutationFn: async (resource: Resource) => {
      if (isResourceWritable(resource)) {
        const { data } = await put(resource)
        return data
      } else {
        throw new Error('Cannot write Resource without required key: id')
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({
        queryKey: ['/admin/resources', resourceId],
      })
      onSuccess?.()
    },
    onError,
  })

  return mutate
}
